import React from "react";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import { Edit, Trash2, Plus, MessageSquareQuote, HelpCircle, GripVertical } from "lucide-react";
import { DECISION_LABELS } from "@/pages/hotline/constants";

const BRANCH_LABEL = { yes: "If YES", no: "If NO", na: "If N/A" };
const BRANCH_COLOR = {
  yes: { bg: "#DCFCE7", text: "#166534" },
  no: { bg: "#FEE2E2", text: "#991B1B" },
  na: { bg: "#F1F5F9", text: "#475569" },
};
const MAX_DEPTH = 6;

function childrenOf(byParent, parentId, branch) {
  const key = `${parentId || "ROOT"}::${branch || "ROOT"}`;
  return byParent.get(key) || [];
}

function NodeCard({ node, onEdit, onDelete }) {
  const isRec = node.kind === "recommendation";
  return (
    <div className={`border rounded-lg p-3 ${isRec ? "bg-[#EAF8FA] border-[#0B7E8C]/30" : "bg-white border-[#E5E7EB]"}`} data-testid={`node-card-${node.id}`}>
      <div className="flex items-start gap-2">
        <span className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${isRec ? "bg-[#0B7E8C] text-white" : "bg-[#F5F7FA] text-[#0B7E8C]"}`}>
          {isRec ? <MessageSquareQuote className="w-3.5 h-3.5" /> : <HelpCircle className="w-3.5 h-3.5" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280]">{isRec ? "Recommendation" : "Question"}</div>
          <div className="text-sm text-[#1F2937] font-medium mt-0.5">{node.text || <span className="text-[#94A3B8] italic">Untitled</span>}</div>
          {isRec && node.suggested_decision && (
            <div className="text-xs text-[#0B7E8C] mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-[#0B7E8C]/20">
              Suggests: {DECISION_LABELS[node.suggested_decision] || node.suggested_decision}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onEdit(node)} className="w-7 h-7 rounded hover:bg-[#F5F7FA] flex items-center justify-center text-[#0B7E8C]" data-testid={`node-edit-${node.id}`}>
            <Edit className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(node)} className="w-7 h-7 rounded hover:bg-[#EF444412] flex items-center justify-center text-[#EF4444]" data-testid={`node-del-${node.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function DraggableNode({ node, idx, onEdit, onDelete }) {
  return (
    <Draggable draggableId={node.id} index={idx}>
      {(p) => (
        <div ref={p.innerRef} {...p.draggableProps} className="flex items-start gap-2">
          <div {...p.dragHandleProps} className="mt-3 cursor-grab active:cursor-grabbing text-[#94A3B8] hover:text-[#0B7E8C]" data-testid={`drag-${node.id}`}>
            <GripVertical className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <NodeCard node={node} onEdit={onEdit} onDelete={onDelete} />
          </div>
        </div>
      )}
    </Draggable>
  );
}

// Renders a single branch (yes/no/na) under a parent node, including its children.
// Uses React.createElement for recursion to keep dev-mode plugins happy.
function BranchSection({ parentNode, branch, byParent, onEdit, onDelete, onAddChild, depth }) {
  const kids = childrenOf(byParent, parentNode.id, branch);
  const bc = BRANCH_COLOR[branch];
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded" style={{ background: bc.bg, color: bc.text }}>{BRANCH_LABEL[branch]}</span>
        <div className="h-px flex-1 bg-[#E5E7EB]" />
        <button onClick={() => onAddChild(parentNode.id, branch)} className="text-xs text-[#0B7E8C] hover:underline inline-flex items-center gap-1" data-testid={`node-add-${parentNode.id}-${branch}`}>
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      {kids.length > 0 && (
        <Droppable droppableId={`branch:${parentNode.id}:${branch}`}>
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
              {kids.map((child, idx) => (
                <div key={child.id}>
                  <DraggableNode node={child} idx={idx} onEdit={onEdit} onDelete={onDelete} />
                  {child.kind !== "recommendation" && depth + 1 < MAX_DEPTH && (
                    <div className="pl-7 mt-2 border-l-2 border-dashed border-[#E5E7EB] ml-3 space-y-2 pb-1">
                      {["yes", "no", "na"].map((b) =>
                        React.createElement(BranchSection, {
                          key: b,
                          parentNode: child,
                          branch: b,
                          byParent, onEdit, onDelete, onAddChild,
                          depth: depth + 1,
                        })
                      )}
                    </div>
                  )}
                </div>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      )}
    </div>
  );
}

export default function ProtocolTreeView({ roots, byParent, onEdit, onDelete, onAddChild }) {
  return (
    <Droppable droppableId="branch:ROOT:ROOT">
      {(provided) => (
        <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
          {roots.map((r, idx) => (
            <div key={r.id}>
              <DraggableNode node={r} idx={idx} onEdit={onEdit} onDelete={onDelete} />
              {r.kind !== "recommendation" && (
                <div className="pl-7 mt-2 border-l-2 border-dashed border-[#E5E7EB] ml-3 space-y-2 pb-1">
                  {["yes", "no", "na"].map((b) => (
                    <BranchSection key={b} parentNode={r} branch={b} byParent={byParent} onEdit={onEdit} onDelete={onDelete} onAddChild={onAddChild} depth={1} />
                  ))}
                </div>
              )}
            </div>
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
}
